import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate, formatTime, timeRange } from "@/lib/dates";

const movedFrom = (m) => (m ? `was ${formatDate(m.date)} ${formatTime(m.start_time)}${m.location ? ` at ${m.location}` : ""}` : "");

export const StatusTag = ({ event }) => {
  if (event.status === "scheduled") return null;
  const canceled = event.status === "canceled";
  return (
    <div className="status-tag" data-testid="event-status-tag">
      <AlertTriangle size={18} className="shrink-0" />
      <div className="min-w-0">
        <p className="font-heading text-lg font-extrabold uppercase leading-none" data-testid="event-status-label">{canceled ? "Canceled" : "Moved"}</p>
        {!canceled && <p className="text-sm font-bold" data-testid="event-moved-from">Now {formatDate(event.date)} {timeRange(event)} · {movedFrom(event.moved_from)}</p>}
        {event.status_note && <p className="text-sm font-semibold" data-testid="event-status-note">{event.status_note}</p>}
      </div>
    </div>
  );
};

export const AlertBanner = ({ events }) => {
  const alerts = events.filter((e) => e.status !== "scheduled");
  if (!alerts.length) return null;
  return (
    <div className="alert-banner" data-testid="alert-banner">
      <AlertTriangle size={26} className="shrink-0 today-bell" />
      <div className="min-w-0 space-y-1">
        <p className="font-heading text-2xl font-extrabold uppercase leading-none">Schedule change</p>
        {alerts.map((e) => (
          <p key={e.id} className="font-bold" data-testid="alert-event">
            {e.status === "canceled" ? "CANCELED" : "MOVED"}: {e.title} ·{" "}
            {e.status === "canceled" ? formatDate(e.date) : `now ${formatDate(e.date)} ${timeRange(e)}${e.location ? ` at ${e.location}` : ""}`}
            {e.status_note && <span className="font-semibold"> — {e.status_note}</span>}
            {e.status === "canceled" && e.makeup_options?.length > 0 && (
              <span className="font-semibold" data-testid="alert-makeup"> · {e.makeup_picked ? "makeup date set, see schedule" : "vote on a makeup date below"}</span>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

const OPTIONS = [["canceled", "Canceled"], ["moved", "Moved"], ["scheduled", "On as planned"]];

export const StatusDialog = ({ event, onClose, onSave }) => {
  const [f, setF] = useState({ status: event.status === "scheduled" ? "canceled" : event.status, note: event.status_note || "",
    date: event.date, start_time: event.start_time, end_time: event.end_time || "", location: event.location || "",
    makeup: [0, 1].map((i) => event.makeup_options?.[i] || { date: "", start_time: "" }) });
  const locked = !!event.makeup_event_id;
  const setMk = (i, k) => (e) => setF({ ...f, makeup: f.makeup.map((m, j) => (j === i ? { ...m, [k]: e.target.value } : m)) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    const { makeup, ...rest } = f;
    const makeup_options = f.status === "canceled" ? makeup.filter((m) => m.date && m.start_time).map(({ id, date, start_time }) => ({ id, date, start_time })) : [];
    onSave({ ...rest, makeup_options, end_time: f.end_time || null, note: f.note.trim() || null }).finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="status-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">Alert parents</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <p className="font-bold">{event.title} · {formatDate(event.date)}</p>
          <div className="seg-group w-full">
            {OPTIONS.map(([v, l]) => (
              <button type="button" key={v} data-testid={`status-option-${v}`} className={`seg-btn flex-1 ${f.status === v ? "seg-btn-active" : ""}`} onClick={() => setF({ ...f, status: v })}>{l}</button>
            ))}
          </div>
          {f.status === "moved" && (
            <>
              <input data-testid="status-date-input" type="date" className="field-input w-full" required value={f.date} onChange={set("date")} />
              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1"><span className="ctl-label">Start</span>
                  <input data-testid="status-start-input" type="time" className="field-input w-full" required value={f.start_time} onChange={set("start_time")} /></label>
                <label className="space-y-1"><span className="ctl-label">End (optional)</span>
                  <input data-testid="status-end-input" type="time" className="field-input w-full" value={f.end_time} onChange={set("end_time")} /></label>
              </div>
              <input data-testid="status-location-input" className="field-input w-full" maxLength={120} placeholder="New location (optional)" value={f.location} onChange={set("location")} />
            </>
          )}
          {f.status === "canceled" && (
            <div className="space-y-2" data-testid="makeup-editor">
              <span className="ctl-label">Makeup date options (optional, parents vote)</span>
              {locked && <p className="text-sm font-semibold text-[var(--muted)]">Makeup already picked - options are locked.</p>}
              {f.makeup.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
                  <input data-testid={`makeup-date-input-${i}`} type="date" disabled={locked} className="field-input w-full" value={m.date} onChange={setMk(i, "date")} aria-label={`Makeup option ${i + 1} date`} />
                  <input data-testid={`makeup-time-input-${i}`} type="time" disabled={locked} className="field-input w-32" value={m.start_time} onChange={setMk(i, "start_time")} aria-label={`Makeup option ${i + 1} time`} />
                </div>
              ))}
            </div>
          )}
          {f.status !== "scheduled" && (
            <input data-testid="status-note-input" className="field-input w-full" maxLength={200} placeholder="Reason, e.g. Rained out - field is flooded" value={f.note} onChange={set("note")} />
          )}
          <button className="btn-primary w-full" data-testid="status-save-button" disabled={busy}>
            {f.status === "scheduled" ? "Clear alert" : "Send alert to parents"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
